import React, { useState, useEffect } from 'react'

const STORAGE_KEY = 'installPromptSeen_v1'

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

function isInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (isIOS() && !isInstalled() && !localStorage.getItem(STORAGE_KEY)) {
      // Small delay so the main app renders first
      const t = setTimeout(() => setVisible(true), 800)
      return () => clearTimeout(t)
    }
  }, [])

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1')
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '0 0 env(safe-area-inset-bottom, 0)',
      fontFamily: "'Titillium Web', 'Barlow Condensed', sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#0d0b0b',
        borderRadius: '20px 20px 0 0',
        border: '1px solid rgba(255,255,255,0.08)',
        borderBottom: 'none',
        borderTop: '3px solid #e10600',
        padding: '28px 24px 36px',
        boxShadow: '0 -20px 60px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 3, textTransform: 'uppercase', color: '#fff' }}>
              Add to Home Screen
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Get the full app experience</div>
          </div>
          <button onClick={dismiss} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 20, width: 32, height: 32, color: '#888', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 28 }}>

          {/* Step 1 */}
          <Step number={1} label={<>Tap the <strong style={{ color: '#fff' }}>Share</strong> button at the bottom of Safari</>}>
            <ShareIcon />
          </Step>

          {/* Step 2 */}
          <Step number={2} label={<>Scroll down and tap <strong style={{ color: '#fff' }}>"Add to Home Screen"</strong></>}>
            <AddToHomeIcon />
          </Step>

          {/* Step 3 */}
          <Step number={3} label={<>Tap <strong style={{ color: '#fff' }}>"Add"</strong> in the top right corner</>}>
            <AddButton />
          </Step>
        </div>

        {/* CTA */}
        <button
          onClick={dismiss}
          style={{
            width: '100%', padding: '14px',
            background: '#e10600', border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 14, fontWeight: 800,
            letterSpacing: 2, textTransform: 'uppercase',
            cursor: 'pointer',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  )
}

function Step({ number, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      {/* Number */}
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: '#e10600', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 14,
        flexShrink: 0,
      }}>{number}</div>

      {/* Label */}
      <div style={{ flex: 1, fontSize: 14, color: '#aaa', lineHeight: 1.4 }}>{label}</div>

      {/* Visual */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56 }}>
        {children}
      </div>
    </div>
  )
}

// iOS Share button icon
function ShareIcon() {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.07)', borderRadius: 10,
      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid rgba(255,255,255,0.12)',
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L12 15" stroke="#0a84ff" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 6L12 2L16 6" stroke="#0a84ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 12H5C4.44772 12 4 12.4477 4 13V20C4 20.5523 4.44772 21 5 21H19C19.5523 21 20 20.5523 20 20V13C20 12.4477 19.5523 12 19 12H16" stroke="#0a84ff" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </div>
  )
}

// "Add to Home Screen" list item mock
function AddToHomeIcon() {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.07)', borderRadius: 10,
      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid rgba(255,255,255,0.12)',
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="4" stroke="#0a84ff" strokeWidth="2"/>
        <path d="M12 8V16M8 12H16" stroke="#0a84ff" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </div>
  )
}

// "Add" button mock
function AddButton() {
  return (
    <div style={{
      background: 'rgba(10,132,255,0.15)', borderRadius: 8,
      padding: '6px 12px',
      border: '1px solid rgba(10,132,255,0.3)',
      color: '#0a84ff', fontSize: 13, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      Add
    </div>
  )
}
